import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { isModuleEnabled } from "@/auth/modules";
import { NAV_CATALOG_GROUPS } from "@/auth/nav";
import StockOrganizerDialog from "@/ui/StockOrganizerDialog";
import { track } from "@/usage";

interface PageEntry {
  type: "page";
  label: string;
  path: string;
  hint: string;
  icon: string;
  module: string;
}

interface StockEntry {
  type: "stock";
  ts_code: string;
  name: string;
  industry: string | null;
}

interface AskEntry {
  type: "ask";
  question: string;
  ts_code?: string;
  scope_label?: string;
  ts: number;
}

type Entry = PageEntry | StockEntry | AskEntry;

const ASK_HISTORY_KEY = "whaleseek_ask_history";

function loadAskHistory(): AskEntry[] {
  try {
    const raw = localStorage.getItem(ASK_HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.map((h: { question: string; ts: number; ts_code?: string; scope_mode?: string; scope_value?: string }) => ({
      type: "ask" as const,
      question: h.question,
      ts: h.ts,
      ts_code: h.ts_code,
      scope_label: h.scope_mode && h.scope_value ? `${h.scope_mode === "tag" ? "🏷️" : "📁"} ${h.scope_value}` : undefined,
    }));
  } catch {
    return [];
  }
}

/** 页面条目从完整目录派生，收进“更多工具”的页面也可直接搜索。
 *  label 开头的 emoji 抽出来做 icon；hint = 所属菜单组。 */
const PAGES: PageEntry[] = NAV_CATALOG_GROUPS.flatMap((g) => {
  const groupText = g.label.replace(/^\S+\s*/, "") || g.label;
  return g.items.map((it) => {
    const m = it.label.match(/^(\p{Extended_Pictographic}️?)\s*(.*)$/u);
    return {
      type: "page" as const,
      label: m?.[2] || it.label,
      path: it.to,
      hint: `菜单 · ${groupText}`,
      icon: m?.[1] ?? g.label.match(/^(\p{Extended_Pictographic}️?)/u)?.[1] ?? "📄",
      module: it.module,
    };
  });
});

export default function CommandPalette() {
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [organizerCode, setOrganizerCode] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();

  // Cmd+K / Ctrl+K 唤起
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    // 顶栏搜索按钮通过自定义事件唤起（跨组件零耦合）
    const onOpenEvt = () => setOpen(true);
    window.addEventListener("whaleseek:open-cmdk", onOpenEvt);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("whaleseek:open-cmdk", onOpenEvt);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // 防抖
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  // 搜索股票
  const stockQ = useQuery({
    queryKey: ["cmdk-stock", debounced],
    queryFn: () => api.searchStocks(debounced, 15),
    enabled: open && debounced.length >= 1,
  });

  // 持仓 + 自选（首屏快捷）
  const watchQ = useQuery({
    queryKey: ["watchlist"],
    queryFn: () => api.listWatchlist(),
    staleTime: 60_000,
    enabled: open,
  });

  // 读 localStorage 历史问答（每次 open 时刷新）
  const askHistory = useMemo(() => (open ? loadAskHistory() : []), [open]);

  const entries: Entry[] = useMemo(() => {
    const text = debounced.toLowerCase();
    // 页面匹配
    const pages = PAGES.filter((p) =>
      isModuleEnabled(me?.preferences, p.module)
      && (!text || p.label.toLowerCase().includes(text) || p.hint.toLowerCase().includes(text) || p.path.includes(text)),
    );
    // 股票（搜索结果优先）
    let stocks: StockEntry[] = [];
    if (debounced.length >= 1 && stockQ.data) {
      stocks = stockQ.data.map((s) => ({ type: "stock" as const, ts_code: s.ts_code, name: s.name, industry: s.industry }));
    } else if (!debounced && watchQ.data) {
      stocks = watchQ.data.slice(0, 8).map((w) => ({
        type: "stock" as const, ts_code: w.ts_code, name: w.name ?? w.ts_code, industry: w.industry,
      }));
    }
    // 历史问答（按 question 文本匹配，没输入时显示最近 5 条）
    const asks = debounced
      ? askHistory.filter((a) => a.question.toLowerCase().includes(text) || (a.ts_code ?? "").toLowerCase().includes(text))
      : askHistory.slice(0, 5);
    return [...pages, ...stocks, ...asks];
  }, [debounced, stockQ.data, watchQ.data, askHistory, me?.preferences]);

  // 重置 activeIdx 当结果变化
  useEffect(() => {
    setActiveIdx(0);
  }, [entries.length, debounced]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, entries.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && e.shiftKey && entries[activeIdx]?.type === "stock") {
      e.preventDefault();
      setOrganizerCode(entries[activeIdx].ts_code);
      setOpen(false);
    } else if (e.key === "Enter" && entries[activeIdx]) {
      e.preventDefault();
      go(entries[activeIdx]);
    } else if ((e.metaKey || e.altKey) && /^[1-9]$/.test(e.key)) {
      // Cmd/Alt + 1-9 直接选第 N 个（避免与输入数字冲突）
      const idx = parseInt(e.key) - 1;
      if (entries[idx]) {
        e.preventDefault();
        go(entries[idx]);
      }
    }
  };

  const go = (entry: Entry) => {
    setOpen(false);
    if (entry.type === "page") {
      track("cmdk_jump", entry.path, { kind: "page" });
      nav(entry.path);
    } else if (entry.type === "stock") {
      track("cmdk_jump", `/stocks/${entry.ts_code}`, { kind: "stock", ts_code: entry.ts_code });
      nav(`/stocks/${entry.ts_code}`);
    } else if (entry.type === "ask") {
      track("cmdk_jump", "/ask", { kind: "ask" });
      nav("/ask");
    }
  };

  if (!open) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-20 px-3 py-2 rounded-full bg-zinc-100 dark:bg-zinc-800 text-xs text-zinc-600 dark:text-zinc-300 shadow-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition z-30 hidden md:flex items-center gap-2"
          title="快速搜索（Cmd+K / Ctrl+K）"
        >
          🔍 <kbd className="px-1.5 py-0.5 text-[10px] bg-white dark:bg-zinc-900 rounded border dark:border-zinc-700">⌘K</kbd>
        </button>
        {organizerCode && <StockOrganizerDialog tsCodes={[organizerCode]} onClose={() => setOrganizerCode(null)} />}
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 pt-[10vh]" onClick={() => setOpen(false)}>
      <div
        className="bg-white dark:bg-zinc-900 rounded-lg border dark:border-zinc-800 shadow-2xl max-w-xl w-full max-h-[70vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b dark:border-zinc-800 px-3 py-2 flex items-center gap-2">
          <span className="text-zinc-400">🔍</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="搜索页面 / 股票（代码/名称/拼音首字母）"
            className="flex-1 outline-none bg-transparent text-sm"
          />
          <kbd className="px-1.5 py-0.5 text-xs bg-zinc-100 dark:bg-zinc-800 rounded border dark:border-zinc-700 text-zinc-500">ESC</kbd>
        </div>

        <div className="flex-1 overflow-y-auto">
          {entries.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">无匹配</div>
          )}
          {entries.map((entry, i) => (
            <button
              key={entry.type === "page" ? `p:${entry.path}` : entry.type === "stock" ? `s:${entry.ts_code}` : `a:${entry.ts}`}
              onClick={() => go(entry)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 ${i === activeIdx ? "bg-blue-50 dark:bg-blue-950/30" : "hover:bg-zinc-50 dark:hover:bg-zinc-800/60"}`}
            >
              {i < 9 && (
                <kbd className="text-[10px] px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-400 font-mono shrink-0 w-7 text-center" title={`${navigator.platform.includes("Mac") ? "⌘" : "Alt"}+${i + 1}`}>
                  {i + 1}
                </kbd>
              )}
              {entry.type === "page" ? (
                <>
                  <span className="text-lg w-6 text-center shrink-0">{entry.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{entry.label}</div>
                    <div className="text-xs text-zinc-400 truncate">{entry.hint}</div>
                  </div>
                  <span className="text-xs text-zinc-400 font-mono shrink-0">{entry.path}</span>
                </>
              ) : entry.type === "stock" ? (
                <>
                  <span className="text-lg w-6 text-center shrink-0">📊</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      {entry.name}
                      <span className="ml-2 text-xs text-zinc-400 font-mono">{entry.ts_code}</span>
                    </div>
                    {entry.industry && <div className="text-xs text-zinc-400">{entry.industry}</div>}
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0">详情 → · ⇧Enter 整理</span>
                </>
              ) : (
                <>
                  <span className="text-lg w-6 text-center shrink-0">🕘</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{entry.question}</div>
                    <div className="text-xs text-zinc-400 flex items-center gap-2">
                      {entry.ts_code && <span className="font-mono text-blue-600">{entry.ts_code}</span>}
                      {entry.scope_label && <span className="text-amber-600">{entry.scope_label}</span>}
                      <span>{new Date(entry.ts).toLocaleString("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0">历史问答 →</span>
                </>
              )}
            </button>
          ))}
        </div>

        <div className="border-t dark:border-zinc-800 px-3 py-1.5 text-xs text-zinc-400 flex items-center gap-3 flex-wrap">
          <span><kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">↑↓</kbd> 选择</span>
          <span><kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">Enter</kbd> 打开</span>
          <span><kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">⇧Enter</kbd> 整理股票</span>
          <span><kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">{navigator.platform.includes("Mac") ? "⌘" : "Alt"}+1-9</kbd> 直接选</span>
          <span><kbd className="px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded">ESC</kbd> 关闭</span>
        </div>
      </div>
    </div>
  );
}
